import React, { useCallback, useState, useEffect } from 'react';
import { SchemePaymentRequestPayload, PaymentResponse } from 'shared/types/cart/Payment';
import useUnzer from 'frontastic/hooks/useUnzer';
import { ApplePayData, EPSUnzerData, GooglepayData, PFCardData, SchemeData, TwintUnzerData } from '../types';

interface UnzerContextConfig {
  orderId: string;
  setOrderId: (id: string) => void;
  makePayment: (
    data: Omit<SchemePaymentRequestPayload, 'paymentMethod'>,
    paymentData: SchemeData,
  ) => Promise<PaymentResponse>;
  createInvoicePayment: () => Promise<any>;
  makeInvoicePayment: (
    data: Omit<SchemePaymentRequestPayload, 'paymentMethod'>,
    paymentData: SchemeData,
  ) => Promise<PaymentResponse>;
  makeInstallmentPayment: (
    data: Omit<SchemePaymentRequestPayload, 'paymentMethod'>,
    paymentData: SchemeData,
  ) => Promise<PaymentResponse>;
  makeEPSPayment: (
    data: Omit<SchemePaymentRequestPayload, 'paymentMethod'>,
    paymentData: EPSUnzerData,
  ) => Promise<PaymentResponse>;
  makeTwintPayment: (
    data: Omit<SchemePaymentRequestPayload, 'paymentMethod'>,
    paymentData: TwintUnzerData,
  ) => Promise<PaymentResponse>;
  makeGooglePayPayment: (
    data: Omit<SchemePaymentRequestPayload, 'paymentMethod'>,
    paymentData: GooglepayData,
  ) => Promise<PaymentResponse>;
  makePFCardPayment: (
    data: Omit<SchemePaymentRequestPayload, 'paymentMethod'>,
    paymentData: PFCardData,
  ) => Promise<PaymentResponse>;
  makePFFinancePayment: (
    data: Omit<SchemePaymentRequestPayload, 'paymentMethod'>,
    paymentData: PFCardData,
  ) => Promise<PaymentResponse>;
  makeApplePayPayment: (
    data: Omit<SchemePaymentRequestPayload, 'paymentMethod'>,
    paymentData: ApplePayData,
  ) => Promise<PaymentResponse>;
}

export const UnzerContext = React.createContext<UnzerContextConfig>({} as UnzerContextConfig);

const UnzerPaymentProvider = ({ children }: React.PropsWithChildren) => {
  const [orderId, setOrderId] = useState('');

  const {
    makePayment: makeUnzerPayment,
    createInvoicePayment: createUnzerInvoicePayment,
    makeInvoicePayment: makeUnzerInvoicePayment,
    makeInstallmentPayment: makeUnzerInstallmentPayment,
    makeEPSPayment: createUnzerEPSPayment,
    makeTwintPayment: createUnzerTwintPayment,
    makeGooglePayPayment: createGooglePayment,
    makePFCardPayment: makeunzerPFCardPayment,
    makePFFinancePayment: makeunzerPFFinancePayment,
    makeApplePayPayment: createApplePayPayment,
  } = useUnzer();

  const makePayment = useCallback(
    async (data: Omit<SchemePaymentRequestPayload, 'paymentMethod'>, paymentData: SchemeData) => {
      const response = await makeUnzerPayment({
        ...data,
        paymentMethod: {
          ...paymentData,
        } as SchemeData,
      } as SchemePaymentRequestPayload);
      return response;
    },
    [makeUnzerPayment], //eslint-disable-line
  );

  const makeEPSPayment = useCallback(
    async (data: Omit<SchemePaymentRequestPayload, 'paymentMethod'>, paymentData: EPSUnzerData) => {
      const response = await createUnzerEPSPayment({
        ...data,
        paymentMethod: {
          ...paymentData,
        } as EPSUnzerData,
      } as unknown as SchemePaymentRequestPayload);

      return response;
    },
    [createUnzerEPSPayment], // Add external dependencies here if necessary
  );

  const makeTwintPayment = useCallback(
    async (data: Omit<SchemePaymentRequestPayload, 'paymentMethod'>, paymentData: TwintUnzerData) => {
      const response = await createUnzerTwintPayment({
        ...data,
        paymentMethod: {
          ...paymentData,
        } as TwintUnzerData,
      } as unknown as SchemePaymentRequestPayload);

      return response;
    },
    [createUnzerTwintPayment], // Add external dependencies here if necessary
  );

  const makeGooglePayPayment = useCallback(
    async (data: Omit<SchemePaymentRequestPayload, 'paymentMethod'>, paymentData: GooglepayData) => {
      const response = await createGooglePayment({
        ...data,
        paymentMethod: {
          ...paymentData,
        } as GooglepayData,
      } as unknown as SchemePaymentRequestPayload);

      return response;
    },
    [createGooglePayment],
  );

  const createInvoicePayment = useCallback(async () => {
    const response = await createUnzerInvoicePayment();
    return response;
  }, [createUnzerInvoicePayment]);

  const makeInvoicePayment = useCallback(
    async (data: Omit<SchemePaymentRequestPayload, 'paymentMethod'>, paymentData: SchemeData) => {
      const response = await makeUnzerInvoicePayment({
        ...data,
        paymentMethod: {
          ...paymentData,
        } as SchemeData,
      } as SchemePaymentRequestPayload);
      return response;
    },
    [makeUnzerInvoicePayment], //eslint-disable-line
  );

  const makeInstallmentPayment = useCallback(
    async (data: Omit<SchemePaymentRequestPayload, 'paymentMethod'>, paymentData: SchemeData) => {
      const response = await makeUnzerInstallmentPayment({
        ...data,
        paymentMethod: {
          ...paymentData,
        } as SchemeData,
      } as SchemePaymentRequestPayload);
      return response;
    },
    [makeUnzerInvoicePayment], //eslint-disable-line
  );

  const makePFCardPayment = useCallback(
    async (data: Omit<SchemePaymentRequestPayload, 'paymentMethod'>, paymentData: PFCardData) => {
      const response = await makeunzerPFCardPayment({
        ...data,
        paymentMethod: {
          ...paymentData,
        } as PFCardData,
      } as unknown as SchemePaymentRequestPayload);

      return response;
    },
    [makeunzerPFCardPayment],
  );

  const makePFFinancePayment = useCallback(
    async (data: Omit<SchemePaymentRequestPayload, 'paymentMethod'>, paymentData: PFCardData) => {
      const response = await makeunzerPFFinancePayment({
        ...data,
        paymentMethod: {
          ...paymentData,
        } as PFCardData,
      } as unknown as SchemePaymentRequestPayload);

      return response;
    },
    [makeunzerPFFinancePayment],
  );

  const makeApplePayPayment = useCallback(
    async (data: Omit<SchemePaymentRequestPayload, 'paymentMethod'>, paymentData: ApplePayData) => {
      const response = await createApplePayPayment({
        ...data,
        paymentMethod: {
          ...paymentData,
        } as ApplePayData,
      } as unknown as SchemePaymentRequestPayload);

      return response;
    },
    [createApplePayPayment],
  );

  return (
    <UnzerContext.Provider
      value={{
        orderId,
        setOrderId,
        makePayment,
        createInvoicePayment,
        makeInvoicePayment,
        makeInstallmentPayment,
        makeEPSPayment,
        makeTwintPayment,
        makeGooglePayPayment,
        makePFCardPayment,
        makePFFinancePayment,
        makeApplePayPayment,
      }}
    >
      {children}
    </UnzerContext.Provider>
  );
};

export default UnzerPaymentProvider;
